// src/supplier/pages/SupplierProducts.jsx
import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { supplierProductAPI, supplierBusinessAPI } from "../services/supplierApi";
import {
  Package, Loader2, Search, Building2, Clock, Tag
} from "lucide-react";

import { fmtPeso } from "../../utils/formatters";
import ShopSelect from "../../components/ShopSelect";

export default function SupplierProducts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const selectedBiz = searchParams.get("businessId") || "";

  const setSelectedBiz = (val) => {
    if (val) {
      setSearchParams({ businessId: val });
    } else {
      setSearchParams({});
    }
  };

  const fetchBusinesses = useCallback(async () => {
    try {
      const res = await supplierBusinessAPI.list({ limit: 200, status: "ACTIVE" });
      setBusinesses(res?.data || []);
    } catch { setBusinesses([]); }
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (selectedBiz) params.businessId = selectedBiz;
      const res = await supplierProductAPI.list(params);
      setProducts(res?.data || []);
    } catch { setProducts([]); }
    finally { setLoading(false); }
  }, [selectedBiz]);

  useEffect(() => { fetchBusinesses(); }, [fetchBusinesses]);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Group products by business
  const grouped = useMemo(() => {
    const filtered = products.filter(p =>
      !search || p.product_name?.toLowerCase().includes(search.toLowerCase())
        || p.product_sku?.toLowerCase().includes(search.toLowerCase())
    );
    const map = {};
    for (const p of filtered) {
      const key = p.business_id || "unknown";
      if (!map[key]) {
        map[key] = { business_id: p.business_id, business_name: p.business_name || "Unknown Business", items: [] };
      }
      map[key].items.push(p);
    }
    return Object.values(map);
  }, [products, search]);

  const totalFiltered = grouped.reduce((acc, g) => acc + g.items.length, 0);

  return (
    <div className="operations-page">
      <div className="operations-page-header">
        <h1 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">Mapped Products</h1>
        <span className="text-xs text-neutral-400">{totalFiltered} of {products.length} products</span>
      </div>

      {/* Filters */}
      <div className="operations-toolbar">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            placeholder="Search products or SKU…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full rounded-xl border border-neutral-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-blue-400 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
          />
        </div>
        <div className="relative">
          <Building2 className="pointer-events-none absolute left-3 top-1/2 z-10 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
          <ShopSelect
            value={selectedBiz}
            onValueChange={setSelectedBiz}
            options={[
              { value: "", label: "All businesses" },
              ...businesses.map((business) => ({ value: business.business_id, label: business.business_name })),
            ]}
            ariaLabel="Filter products by business"
            align="start"
            minPanelWidth={208}
            triggerClassName="min-h-10 w-52 rounded-xl py-2 pl-9 pr-3 text-sm"
            panelClassName="py-1"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      ) : totalFiltered === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-200 dark:border-neutral-700 p-8 text-center">
          <Package className="mx-auto h-8 w-8 text-neutral-300 dark:text-neutral-600 mb-2" />
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
            {products.length === 0 ? "No mapped products yet" : "No products match your filters"}
          </p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
            {products.length === 0
              ? "When businesses link products to your supplier account, they'll appear here."
              : "Try adjusting your search or business filter."}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(group => (
            <div key={group.business_id}>
              {/* Business group header */}
              {!selectedBiz && (
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-3.5 w-3.5 text-blue-500" />
                  <span className="text-xs font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                    {group.business_name}
                  </span>
                  <span className="text-[10px] text-neutral-400">({group.items.length})</span>
                </div>
              )}

              <div className="space-y-2">
                {group.items.map(p => (
                  <div
                    key={p.mapping_id}
                    className="operations-surface p-4"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <Package className="h-4 w-4 text-blue-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-neutral-900 dark:text-neutral-50 truncate">
                          {p.product_name}
                        </span>
                      </div>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold flex-shrink-0 ${
                        p.product_status === "LISTED"
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                          : p.product_status === "DRAFT"
                          ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                          : "bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400"
                      }`}>
                        {p.product_status || "—"}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                      <div>
                        <p className="text-neutral-400 dark:text-neutral-500 text-[10px] mb-0.5">SKU</p>
                        <p className="font-mono text-neutral-700 dark:text-neutral-200">{p.product_sku || "—"}</p>
                      </div>
                      <div>
                        <p className="text-neutral-400 dark:text-neutral-500 text-[10px] mb-0.5">Unit Cost</p>
                        <p className="font-medium text-neutral-700 dark:text-neutral-200">
                          {p.unit_cost_cents != null ? fmtPeso(p.unit_cost_cents) : "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-neutral-400 dark:text-neutral-500 text-[10px] mb-0.5">Lead Time</p>
                        <p className="text-neutral-700 dark:text-neutral-200 flex items-center gap-1">
                          <Clock className="h-3 w-3 text-neutral-400" />
                          {p.lead_time_days != null ? `${p.lead_time_days} days` : "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-neutral-400 dark:text-neutral-500 text-[10px] mb-0.5">Min Order Qty</p>
                        <p className="text-neutral-700 dark:text-neutral-200">{p.min_order_qty ?? "—"}</p>
                      </div>
                      <div>
                        <p className="text-neutral-400 dark:text-neutral-500 text-[10px] mb-0.5">Preferred</p>
                        <p className="text-neutral-700 dark:text-neutral-200">
                          {p.is_preferred ? (
                            <span className="text-amber-600 dark:text-amber-400 flex items-center gap-1">
                              <Tag className="h-3 w-3" /> Yes
                            </span>
                          ) : "No"}
                        </p>
                      </div>
                    </div>

                    {selectedBiz ? null : (
                      <div className="mt-2 text-[10px] text-neutral-400 dark:text-neutral-500">
                        <Building2 className="inline h-3 w-3 mr-0.5 align-text-bottom" />{p.business_name}
                      </div>
                    )}

                    {p.mapping_notes && (
                      <div className="mt-2 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 p-2 text-xs text-neutral-600 dark:text-neutral-300">
                        {p.mapping_notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
